import { userName } from '@/components/features/CalculationRequests/tables/calculation-request-badges';
import { CalculationRequestCard } from '@/components/features/CalculationRequests/tables/CalculationRequestCard';
import { CalculationRequestTable } from '@/components/features/CalculationRequests/tables/CalculationRequestTable';
import type { CalculationRequest, CalculationRequestOptions } from '@/components/features/CalculationRequests/types';
import { CalculationRequestSelect, type CalculationRequestOption } from '@/components/global/selectors/CalculationRequestSelect';
import { GenericToolbar, type ToolbarViewMode } from '@/components/ui/generic-toolbar/GenericToolbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { useColumnVisibility } from '@/hooks/use-column-visibility';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { settingLabel } from '@/lib/status-labels';
import { router } from '@inertiajs/react';
import { Calculator } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

/** The option lists the table needs — the tab has no use for the rest. */
type LeadCalculationTableOptions = Pick<CalculationRequestOptions, 'statuses' | 'phases' | 'complexities' | 'engineers'>;

interface LeadCalculationRequestsProps {
    leadId: string;
    /** Calculation requests currently attached to this lead. */
    linked: CalculationRequest[];
    /** The pick list — deferred, so it is absent on first paint. */
    options: CalculationRequestOption[];
    /** Status/phase/complexity catalogues and the engineer list, for the table. */
    tableOptions: LeadCalculationTableOptions;
    canViewFinancials: boolean;
    canLink: boolean;
    canUpdateStatus: boolean;
    canAssign: boolean;
}

/**
 * Columns the tab can render. The queue's `lead` column is dropped — every row
 * here belongs to the lead being viewed.
 */
const ALL_COLUMN_IDS = [
    'request_number',
    'object_name',
    'project_phase',
    'status',
    'assignee',
    'submitted_by',
    'requested_calculation_date',
    'submitted_at',
    'completed_at',
    'complexity',
    'hours_spent',
    'turnaround',
    'calculated_amount',
    'sold_amount',
    'outcome',
    'project',
    'created_at',
] as const;

/** Narrower than the queue's default set: the tab is a column, not a full page. */
const DEFAULT_VISIBLE_COLUMNS = [
    'request_number',
    'object_name',
    'project_phase',
    'status',
    'assignee',
    'requested_calculation_date',
    'calculated_amount',
] as const;

const COLUMN_STORAGE_KEY = 'leads:show:calculations';

/** Values sortable as text; everything else is compared as a number. */
const TEXT_SORT_COLUMNS = new Set(['request_number', 'object_name', 'status', 'project_phase', 'complexity', 'outcome']);

/** Columns the toolbar can sort on — the queue's list, minus the columns this tab drops. */
const SORTABLE_COLUMN_IDS = new Set([
    'request_number',
    'object_name',
    'status',
    'requested_calculation_date',
    'submitted_at',
    'completed_at',
    'calculated_amount',
    'sold_amount',
    'hours_spent',
    'created_at',
]);

/** The value a row contributes to a filter column, matched against the selected values. */
function filterValue(request: CalculationRequest, columnId: string): string {
    switch (columnId) {
        case 'status':
            return request.status ?? '';
        case 'project_phase':
            return request.project_phase ?? '';
        case 'complexity':
            return request.complexity ?? '';
        case 'outcome':
            return request.outcome ?? '';
        // Mirrors the queue's "Unassigned" pseudo-value.
        case 'assignee':
            return request.assigned_to_user_id ?? 'unassigned';
        case 'submitted_by':
            return request.submitted_by_user_id ?? '';
        default:
            return '';
    }
}

/** Everything the search box matches a row against. */
function searchHaystack(request: CalculationRequest): string {
    return [
        request.request_number,
        request.object_name,
        request.company?.name,
        request.system,
        request.assignee ? userName(request.assignee) : null,
        request.project?.contract_number,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
}

/**
 * The lead's calculations, on their own tab.
 *
 * Two ways in, matching the two ways a calculation can come about: raise a new one
 * against this lead, or attach one that already exists.
 */
export default function LeadCalculationRequests({
    leadId,
    linked,
    options,
    tableOptions,
    canViewFinancials,
    canLink,
    canUpdateStatus,
    canAssign,
}: LeadCalculationRequestsProps) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
    const [viewMode, setViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [search, setSearch] = useState('');
    const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const {
        visibleColumns,
        showColumn: handleColumnShow,
        hideColumn: handleColumnHide,
    } = useColumnVisibility(COLUMN_STORAGE_KEY, DEFAULT_VISIBLE_COLUMNS, ALL_COLUMN_IDS);

    useEffect(() => {
        setViewMode(isMobile ? 'grid' : 'table');
    }, [isMobile]);

    const linkedIds = useMemo(() => linked.map((request) => request.id), [linked]);

    const columnDefinitions = useMemo(
        () => [
            { id: 'request_number', label: t('calculation_requests.tables.request_number', 'Number') },
            { id: 'object_name', label: t('calculation_requests.tables.object_name', 'Object') },
            { id: 'project_phase', label: t('calculation_requests.tables.project_phase', 'Phase') },
            { id: 'status', label: t('calculation_requests.tables.status', 'Status') },
            { id: 'assignee', label: t('calculation_requests.tables.assignee', 'Engineer') },
            { id: 'submitted_by', label: t('calculation_requests.tables.submitted_by', 'Created by') },
            { id: 'requested_calculation_date', label: t('calculation_requests.tables.requested_calculation_date', 'Needed by') },
            { id: 'submitted_at', label: t('calculation_requests.tables.submitted_at', 'Submitted') },
            { id: 'completed_at', label: t('calculation_requests.tables.completed_at', 'Completed') },
            { id: 'complexity', label: t('calculation_requests.tables.complexity', 'Complexity') },
            { id: 'hours_spent', label: t('calculation_requests.tables.hours_spent', 'Hours') },
            { id: 'turnaround', label: t('calculation_requests.tables.turnaround', 'Turnaround') },
            { id: 'calculated_amount', label: t('calculation_requests.tables.calculated_amount', 'Calculated') },
            { id: 'sold_amount', label: t('calculation_requests.tables.sold_amount', 'Sold') },
            { id: 'outcome', label: t('calculation_requests.tables.outcome', 'Outcome') },
            { id: 'project', label: t('calculation_requests.tables.project', 'Project') },
            { id: 'created_at', label: t('calculation_requests.tables.created_at', 'Created') },
        ],
        [t],
    );

    const quickFilterDefinitions = useMemo(
        () => [
            { id: 'status', label: t('calculation_requests.index.quick_filters.status', 'Status') },
            { id: 'assignee', label: t('calculation_requests.index.quick_filters.assignee', 'Engineer') },
            { id: 'outcome', label: t('calculation_requests.index.quick_filters.outcome', 'Outcome') },
        ],
        [t],
    );

    /**
     * Filter catalogues, narrowed to the values this lead's calculations actually carry.
     * The queue offers every configured status because it filters server-side over the
     * whole table; here the set is fixed and small, so an option that can only ever
     * return nothing is noise.
     */
    const columnFilterOptions = useMemo<Record<string, Array<{ value: string; label: string }>>>(() => {
        const present = (columnId: string): Set<string> => new Set(linked.map((request) => filterValue(request, columnId)).filter(Boolean));

        const fromCatalogue = (columnId: string, catalogue: CalculationRequestOptions['statuses'], namespace: string) => {
            const values = present(columnId);

            return catalogue
                .filter((option) => values.has(option.value))
                .map((option) => ({ value: option.value, label: settingLabel(t, namespace, option.value) || option.label }));
        };

        const assigneeValues = present('assignee');
        const submittedByValues = present('submitted_by');
        const engineerOptions = (values: Set<string>) =>
            tableOptions.engineers
                .filter((engineer) => values.has(engineer.id))
                .map((engineer) => ({ value: engineer.id, label: userName(engineer) }));

        return {
            status: fromCatalogue('status', tableOptions.statuses, 'calculation_request_statuses'),
            project_phase: fromCatalogue('project_phase', tableOptions.phases, 'calculation_request_phases'),
            complexity: fromCatalogue('complexity', tableOptions.complexities, 'calculation_request_complexities'),
            outcome: Array.from(present('outcome')).map((outcome) => ({
                value: outcome,
                label: t(`calculation_requests.outcomes.${outcome}`, outcome),
            })),
            assignee: [
                ...(assigneeValues.has('unassigned')
                    ? [{ value: 'unassigned', label: t('calculation_requests.index.filters.unassigned', 'Unassigned') }]
                    : []),
                ...engineerOptions(assigneeValues),
            ],
            submitted_by: engineerOptions(submittedByValues),
        };
    }, [linked, tableOptions, t]);

    const sortableColumns = useMemo(() => columnDefinitions.filter((column) => SORTABLE_COLUMN_IDS.has(column.id)), [columnDefinitions]);

    const handleFilterChange = (columnId: string, values: string[]) => {
        setColumnFilters((previous) => ({ ...previous, [columnId]: values }));
    };

    const toggleSingleFilter = (columnId: string, value: string) => {
        setColumnFilters((previous) => {
            const current = previous[columnId] ?? [];

            return { ...previous, [columnId]: current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value] };
        });
    };

    const clearFilters = () => {
        setColumnFilters({});
        setSearch('');
    };

    const clearSort = () => {
        setSortColumn(null);
        setSortDirection(null);
    };

    const activeQuickFilterNames = quickFilterDefinitions.filter((definition) => (columnFilters[definition.id] ?? []).length > 0).map((d) => d.label);
    const quickFilterButtonLabel =
        activeQuickFilterNames.length > 0
            ? `${t('calculation_requests.index.toolbar.quick_filters', 'Quick filters')}: ${activeQuickFilterNames.join(', ')}`
            : t('calculation_requests.index.toolbar.quick_filters', 'Quick filters');

    /**
     * Filtering, like sorting, is local: the tab already holds the lead's whole set, so
     * a round trip would only re-fetch the rows it has.
     */
    const filteredRequests = useMemo(() => {
        const term = search.trim().toLowerCase();

        return linked.filter((request) => {
            if (term && !searchHaystack(request).includes(term)) {
                return false;
            }

            return Object.entries(columnFilters).every(
                ([columnId, values]) => values.length === 0 || values.includes(filterValue(request, columnId)),
            );
        });
    }, [linked, search, columnFilters]);

    /**
     * Whatever is already attached is merged into the candidate list regardless of
     * what the (deferred) pick list carries, so an attached calculation is always
     * representable — and therefore detachable — even before the options arrive.
     */
    const candidates = useMemo(() => {
        const byId = new Map<string, CalculationRequestOption>();

        const attached: CalculationRequestOption[] = linked.map((request) => ({
            id: request.id,
            request_number: request.request_number,
            object_name: request.object_name,
            status: request.status,
            company_name: request.company?.name ?? null,
            lead_id: request.lead_id,
            project_id: request.project_id,
        }));

        [...options, ...attached].forEach((request) => byId.set(request.id, request));

        return Array.from(byId.values()).map((request) => ({
            ...request,
            note:
                request.lead_id && request.lead_id !== leadId ? t('leads.sidebar.calculation_request_other_lead', 'Attached to another lead') : null,
        }));
    }, [options, linked, leadId, t]);

    /** Sorting is local for the same reason the filtering above is. */
    const sortedRequests = useMemo(() => {
        if (!sortColumn || !sortDirection) {
            return filteredRequests;
        }

        const direction = sortDirection === 'asc' ? 1 : -1;

        const value = (request: CalculationRequest): string | number | null => {
            switch (sortColumn) {
                case 'turnaround':
                    return request.turnaround_days;
                case 'hours_spent':
                    return request.hours_spent === null || request.hours_spent === undefined ? null : Number(request.hours_spent);
                case 'calculated_amount':
                    return request.calculated_amount === null ? null : Number(request.calculated_amount);
                case 'sold_amount':
                    return request.sold_amount === null ? null : Number(request.sold_amount);
                case 'assignee':
                    return [request.assignee?.first_name, request.assignee?.last_name].filter(Boolean).join(' ') || null;
                case 'submitted_by':
                    return [request.submitted_by?.first_name, request.submitted_by?.last_name].filter(Boolean).join(' ') || null;
                case 'project':
                    return request.project?.contract_number ?? null;
                default:
                    return (request[sortColumn as keyof CalculationRequest] as string | number | null | undefined) ?? null;
            }
        };

        return [...filteredRequests].sort((a, b) => {
            const left = value(a);
            const right = value(b);

            // Empty cells sink to the bottom whichever way the column is sorted.
            if (left === null || left === '') {
                return right === null || right === '' ? 0 : 1;
            }
            if (right === null || right === '') {
                return -1;
            }

            if (TEXT_SORT_COLUMNS.has(sortColumn) || typeof left === 'string') {
                return String(left).localeCompare(String(right)) * direction;
            }

            return (Number(left) - Number(right)) * direction;
        });
    }, [filteredRequests, sortColumn, sortDirection]);

    /**
     * The endpoint takes the complete desired set, so the whole list goes with every
     * toggle — the picker stays open and each pick supersedes the last.
     */
    const sync = (ids: string[]) => {
        router.patch(
            route('leads.calculation-requests.sync', leadId),
            { calculation_request_ids: ids },
            {
                preserveScroll: true,
                onSuccess: () => toast.success(t('leads.flash.calculation_requests_updated', 'Calculation requests updated.')),
                onError: () => toast.error(t('leads.flash.calculation_requests_failed', 'Failed to update calculation requests.')),
            },
        );
    };

    // Inline edits: PATCH, then let Inertia refresh the lead's props in place.
    const handleUpdate = {
        status: canUpdateStatus
            ? async (id: string, value: string) => {
                  router.patch(
                      `/calculation-requests/${id}/status`,
                      { status: value },
                      {
                          preserveState: true,
                          preserveScroll: true,
                          onSuccess: () => toast.success(t('calculation_requests.index.messages.status_updated', 'Status updated')),
                          onError: () => toast.error(t('calculation_requests.index.messages.failed_update_status', 'Failed to update status')),
                      },
                  );
              }
            : undefined,
        assignee: canAssign
            ? async (id: string, userId: string | null) => {
                  router.patch(
                      `/calculation-requests/${id}/assign`,
                      { assigned_to_user_id: userId },
                      {
                          preserveState: true,
                          preserveScroll: true,
                          onSuccess: () => toast.success(t('calculation_requests.index.messages.assignee_updated', 'Engineer updated')),
                          onError: () => toast.error(t('calculation_requests.index.messages.failed_update_assignee', 'Failed to update engineer')),
                      },
                  );
              }
            : undefined,
    };

    return (
        <Card>
            <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 pb-3">
                <div className="space-y-1">
                    <CardTitle>{t('leads.calculations.title', 'Calculation requests')}</CardTitle>
                    <CardDescription>
                        {t('leads.calculations.subtitle', 'Everything engineering has been asked to price for this lead.')}
                    </CardDescription>
                </div>
                {canLink ? (
                    <CalculationRequestSelect
                        multiple
                        values={linkedIds}
                        onValuesChange={sync}
                        requests={candidates}
                        variant="field"
                        className="w-auto"
                        labels={{
                            // Pinned, so the trigger keeps reading as an action rather than
                            // echoing the numbers the table below already lists.
                            trigger: t('leads.calculations.attach', 'Attach existing'),
                            placeholder: t('leads.calculations.attach', 'Attach existing'),
                            search: t('leads.sidebar.calculation_requests_search', 'Search calculations…'),
                            empty: t('leads.sidebar.calculation_requests_empty', 'No calculation requests found.'),
                            heading: t('leads.sidebar.calculation_requests', 'Calculation requests'),
                            selected: t('leads.sidebar.calculation_requests_selected', 'calculations'),
                        }}
                    />
                ) : null}
            </CardHeader>
            <CardContent>
                {linked.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-10 text-center">
                        <Calculator className="text-muted-foreground h-8 w-8" />
                        <p className="text-sm font-medium">{t('leads.calculations.empty_title', 'No calculations yet')}</p>
                        <p className="text-muted-foreground max-w-md text-sm">
                            {t('leads.calculations.empty_description', 'Raise a calculation to have engineering price this lead.')}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* The queue's toolbar, filtering the rows in place — there is no
                            page to turn here, so the per-page select stays out. */}
                        <GenericToolbar
                            quickFilterButtonLabel={quickFilterButtonLabel}
                            quickFilterDefinitions={quickFilterDefinitions}
                            columnDefinitions={columnDefinitions}
                            columnFilterOptions={columnFilterOptions}
                            columnFilters={columnFilters}
                            columnFilterSearch={columnFilterSearch}
                            setColumnFilterSearch={setColumnFilterSearch}
                            toggleSingleFilter={toggleSingleFilter}
                            handleFilterChange={handleFilterChange}
                            clearFilters={clearFilters}
                            isFilterOpen={isFilterOpen}
                            setIsFilterOpen={setIsFilterOpen}
                            isSortOpen={isSortOpen}
                            setIsSortOpen={setIsSortOpen}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection}
                            handleSortChange={(column, direction) => {
                                setSortColumn(column);
                                setSortDirection(direction);
                            }}
                            clearSort={clearSort}
                            search={search}
                            setSearch={setSearch}
                            showPerPageSelect={false}
                            viewMode={viewMode}
                            setViewMode={setViewMode}
                            visibleColumns={visibleColumns}
                            handleColumnShow={handleColumnShow}
                            handleColumnHide={handleColumnHide}
                            filteredCount={sortedRequests.length}
                            resourceName="calculation_requests"
                            resourceNamePlural={t('calculation_requests.index.title', 'Calculation Requests').toLowerCase()}
                            sortableColumns={sortableColumns}
                        />

                        {sortedRequests.length === 0 ? (
                            <div className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
                                {t('calculation_requests.index.toolbar.no_matches', 'No matching calculation requests')}
                            </div>
                        ) : viewMode === 'grid' ? (
                            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                {sortedRequests.map((request) => (
                                    <CalculationRequestCard
                                        key={request.id}
                                        request={request}
                                        options={tableOptions}
                                        canViewFinancials={canViewFinancials}
                                    />
                                ))}
                            </div>
                        ) : (
                            // Horizontal scroll rather than squeezing: the tab is narrower than
                            // the queue page the columns were sized for.
                            <div className="overflow-x-auto">
                                <CalculationRequestTable
                                    requests={sortedRequests}
                                    options={tableOptions}
                                    onUpdate={handleUpdate}
                                    canViewFinancials={canViewFinancials}
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    onSortChange={(column, direction) => {
                                        setSortColumn(column);
                                        setSortDirection(direction);
                                    }}
                                    visibleColumns={visibleColumns}
                                    onColumnHide={handleColumnHide}
                                    columnFilters={columnFilters}
                                    columnFilterOptions={columnFilterOptions}
                                    onFilterChange={handleFilterChange}
                                />
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
